import { apiUrl } from '../config';

interface ReportPayload {
  type: 'react' | 'window.onerror' | 'unhandledrejection' | 'api';
  message: string;
  stack?: string;
  url?: string;
  route?: string;
  userAgent?: string;
  userEmail?: string;
  userId?: string | number;
  context?: Record<string, unknown>;
}

interface WirePayload {
  type: string;
  message: string;
  stack?: string;
  url?: string;
  route?: string;
  userAgent?: string;
  userEmail?: string;
  userId?: string;
  context?: string;
}

// Client-side dedupe so a render loop does not flood the network.
const recentlySent = new Map<string, number>();
const DEDUPE_WINDOW_MS = 60_000;

function shouldSend(key: string): boolean {
  const now = Date.now();
  const last = recentlySent.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;
  recentlySent.set(key, now);
  if (recentlySent.size > 200) {
    for (const [k, t] of recentlySent) {
      if (now - t > DEDUPE_WINDOW_MS) recentlySent.delete(k);
    }
  }
  return true;
}

function readUserFromStorage(): { email?: string; id?: string | number } {
  try {
    const token = localStorage.getItem('accessToken');
    if (token) {
      const parts = token.split('.');
      if (parts.length >= 2) {
        const payload = JSON.parse(
          atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
        );
        return { email: payload?.sub, id: payload?.id ?? payload?.userId };
      }
    }
  } catch {}
  return {};
}

export function reportClientError(payload: ReportPayload): void {
  try {
    const user = readUserFromStorage();
    const body: WirePayload = {
      type: payload.type,
      message: payload.message,
      stack: payload.stack,
      url: payload.url ?? window.location.href,
      route: payload.route ?? window.location.pathname,
      userAgent: payload.userAgent ?? navigator.userAgent,
      userEmail: payload.userEmail ?? user.email,
      userId:
        payload.userId !== undefined
          ? String(payload.userId)
          : user.id !== undefined
          ? String(user.id)
          : undefined,
      context: payload.context ? JSON.stringify(payload.context) : undefined
    };

    const dedupeKey = `${body.type}|${body.message}|${body.route}|${body.userEmail ?? ''}`;
    if (!shouldSend(dedupeKey)) return;

    fetch(`${apiUrl}client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true
    }).catch(() => {
      /* fail silently — never let reporter break the app */
    });
  } catch {
    /* fail silently */
  }
}

let installed = false;
export function installGlobalErrorHandlers(): void {
  if (installed) return;
  installed = true;

  window.addEventListener('error', (event) => {
    reportClientError({
      type: 'window.onerror',
      message: event.message || 'unknown error',
      stack: event.error?.stack,
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportClientError({
      type: 'unhandledrejection',
      message:
        (reason && (reason.message || String(reason))) || 'unhandled promise',
      stack: reason?.stack,
      context: reason?.response
        ? {
            status: reason.response.status,
            url: reason.response.config?.url,
            method: reason.response.config?.method
          }
        : undefined
    });
  });
}
